/* ============================================================================
   Slide 3 — "The Mind" — SHARED CONTENT (single source of truth)
   Both the desktop layout (HTLDSlide3Inner) and the mobile layout
   (HTLDSlide3Mobile) read from here, so text never drifts between them.
   Edit copy ONLY in this file — English and German side by side; the German
   object must have exactly the same shape as the English one.
   ========================================================================== */
import { ByLang, useL } from "i18n/lang";

export const accent3 = "#4fc3f7";

export interface WaveBadge { symbol: string; name: string; desc: string; }

export interface MindLayer {
  key: "conscious" | "preconscious" | "unconscious";
  name: string;
  tag: string;
  color: string;
  highlight?: boolean;
  awareness: string;
  role: string;
  accessibility: string;
  sleep: string;
  waves: WaveBadge[];
  wavesNote?: string;
}

/** Table rows on the desktop slide, in order (keys of MindLayer). */
export const slide3RowKeys = ["awareness", "role", "accessibility"] as const;

const en = {
  layers: [
    {
      key: "conscious", name: "Conscious mind", tag: "SURFACE", color: "#85b7eb",
      awareness: "Fully aware — logical thinking and deliberate attention.",
      role: "Decides, reasons, and directs voluntary actions.",
      accessibility: "Always accessible — you are here right now.",
      sleep: "Active during wakefulness. Quiets as you fall asleep.",
      waves: [ { symbol: "β", name: "Beta", desc: "alert, focused" }, { symbol: "α", name: "Alpha", desc: "relaxed" } ],
    },
    {
      key: "preconscious", name: "Preconscious mind", tag: "BRIDGE", color: accent3, highlight: true,
      awareness: "Semi-aware — stores habits, emotions, and recent memories.",
      role: "Automates behaviours and filters what reaches conscious awareness.",
      accessibility: "Reachable through relaxation, meditation, and light sleep.",
      sleep: "Dominant during REM sleep — where dreams are vivid, felt as real, and open to conscious control.",
      waves: [ { symbol: "α", name: "Alpha", desc: "meditative" }, { symbol: "θ", name: "Theta", desc: "dreaming, REM" } ],
      wavesNote: "where Dreamfulness works",
    },
    {
      key: "unconscious", name: "Unconscious mind", tag: "DEEP CORE", color: "#b5d4f4",
      awareness: "Completely hidden — holds deep instincts, repressed memories, and core beliefs.",
      role: "Shapes personality, deep patterns, and emotional responses.",
      accessibility: "Accessed through deep sleep, Yoga Nidra, and lucid dreaming.",
      sleep: "Dominant during deep Non-REM sleep — where the body and nervous system restore themselves.",
      waves: [ { symbol: "δ", name: "Delta", desc: "deep sleep, restore" } ],
    },
  ] as MindLayer[],
  meta: {
    breadcrumbPre: "The Three Layers",
    breadcrumbAccent: "Where wellness happens",
    titlePlain: "Mind",
    titleAccent: "States",
    headerPlain: "The",
    headerAccent: "Mind",
    /** Direction labels: "← Surface · Waking awareness" … "Deep wellness · Below awareness →" */
    journeySurface: "Surface",
    journeyWaking: "Waking awareness",
    journeyDeep: "Deep wellness",
    journeyBelow: "Below awareness",
    labels: {
      awareness: "Awareness",
      role: "Role",
      accessibility: "Accessibility",
      sleep: "Sleep state",
      waves: "Brain waves",
    },
    /** Separator between wave name and description (mobile). */
    waveSep: " — ",
    footnoteLead: "Why this matters:",
    footnoteBody:
      "Dreamfulness works during REM sleep, where dreams are realised and we learn to control them. " +
      "By becoming conscious within the dream, you access the preconscious mind directly — the bridge " +
      "where habits, emotions, and memories can be explored and better understood.",
  },
  canvas: {
    /** Iceberg labels: above water, at the waterline, deep below. */
    labels: ["Conscious", "Preconscious", "Unconscious"],
  },
};

export type Slide3Content = typeof en;

const de: Slide3Content = {
  layers: [
    {
      key: "conscious", name: "Bewusster Geist", tag: "OBERFLÄCHE", color: "#85b7eb",
      awareness: "Voll bewusst – logisches Denken und gezielte Aufmerksamkeit.",
      role: "Entscheidet, wägt ab und steuert willentliche Handlungen.",
      accessibility: "Immer zugänglich – genau hier bist du jetzt.",
      sleep: "Aktiv im Wachzustand. Wird beim Einschlafen still.",
      waves: [ { symbol: "β", name: "Beta", desc: "wach, fokussiert" }, { symbol: "α", name: "Alpha", desc: "entspannt" } ],
    },
    {
      key: "preconscious", name: "Vorbewusster Geist", tag: "BRÜCKE", color: accent3, highlight: true,
      awareness: "Halb bewusst – speichert Gewohnheiten, Gefühle und frische Erinnerungen.",
      role: "Automatisiert Verhalten und filtert, was ins Bewusstsein gelangt.",
      accessibility: "Erreichbar durch Entspannung, Meditation und leichten Schlaf.",
      sleep: "Dominant im REM-Schlaf – Träume sind lebhaft, fühlen sich echt an und lassen sich bewusst lenken.",
      waves: [ { symbol: "α", name: "Alpha", desc: "meditativ" }, { symbol: "θ", name: "Theta", desc: "Träumen, REM" } ],
      wavesNote: "hier wirkt Dreamfulness",
    },
    {
      key: "unconscious", name: "Unbewusster Geist", tag: "TIEFER KERN", color: "#b5d4f4",
      awareness: "Völlig verborgen – birgt tiefe Instinkte, verdrängte Erinnerungen und Grundüberzeugungen.",
      role: "Prägt Persönlichkeit, tiefe Muster und emotionale Reaktionen.",
      accessibility: "Zugänglich über Tiefschlaf, Yoga Nidra und luzides Träumen.",
      sleep: "Dominant im tiefen Non-REM-Schlaf – hier regenerieren sich Körper und Nervensystem.",
      waves: [ { symbol: "δ", name: "Delta", desc: "Tiefschlaf, Erholung" } ],
    },
  ],
  meta: {
    breadcrumbPre: "Die drei Ebenen",
    breadcrumbAccent: "Wo Wohlbefinden entsteht",
    titlePlain: "Zustände des",
    titleAccent: "Geistes",
    headerPlain: "Der",
    headerAccent: "Geist",
    journeySurface: "Oberfläche",
    journeyWaking: "Waches Gewahrsein",
    journeyDeep: "Tiefes Wohlbefinden",
    journeyBelow: "Jenseits des Gewahrseins",
    labels: {
      awareness: "Bewusstheit",
      role: "Rolle",
      accessibility: "Zugang",
      sleep: "Schlafzustand",
      waves: "Gehirnwellen",
    },
    waveSep: " – ",
    footnoteLead: "Warum das wichtig ist:",
    footnoteBody:
      "Dreamfulness wirkt im REM-Schlaf, wo Träume entstehen und wir lernen, sie zu lenken. " +
      "Wenn du im Traum bewusst wirst, erreichst du direkt den vorbewussten Geist – die Brücke, " +
      "über die sich Gewohnheiten, Gefühle und Erinnerungen erkunden und besser verstehen lassen.",
  },
  canvas: {
    labels: ["Bewusst", "Vorbewusst", "Unbewusst"],
  },
};

export const slide3Content: ByLang<Slide3Content> = { en, de };
export const useSlide3 = () => useL(slide3Content);
